import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Footer from './Footer';

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2
      className="text-neutral-900"
      style={{ fontWeight: 500, letterSpacing: '0.005em', fontSize: 'clamp(1.25rem, 2.4vw, 1.6rem)' }}
    >
      {title}
    </h2>
    <div className="space-y-3 text-[15px] leading-relaxed text-neutral-700">{children}</div>
  </section>
);

const Sub = ({ children }) => (
  <p className="text-[11px] tracking-[0.3em] uppercase font-bold pt-1" style={{ color: '#7d65b8' }}>
    {children}
  </p>
);

const TERMS_SERVICES = [
  { name: 'Signature Soulboard', format: 'Asynchronous / Online', price: '€150 / 55 000 Ft', scope: 'Bespoke 10-page personalised visual and written digital art book synthesized from your birth chart and visual world. Curated over 10 to 15 hours. Delivered as a high-resolution PDF.' },
  { name: 'The Complete Soulboard Journey', format: 'Fully Online', price: '€650 / 250 000 Ft', scope: 'The full Soulboard Method, beginning with the creation of your 10-page Signature Soulboard. Approximately six weeks in total: your Signature Soulboard (weeks 1 to 2) and a 30-min online Introduction Call, followed by the one month guided Soulboard Method (90-min online Blueprint Session, 3 weeks of guided practice with regular feedback via chat/email, a 60-min online Refinement Call, and your personalised Embodied Style Blueprint).' },
  { name: 'The Style Shift', format: 'Hybrid (In-Person & Online) / Hands-on VIP', price: '€750 / 300 000 Ft', scope: 'The ultimate hands-on transformation: Includes the Signature Soulboard, an in-person Wardrobe Edit (3 hours), and either in-person Personal Shopping (3 hours) or a full Private Sourcing Edit, concluding with your custom Wardrobe Blueprint.' },
  { name: 'Wardrobe Edit', format: 'In-Person', price: '€240 / 95 000 Ft', scope: '3-hour intensive in-person wardrobe editing, decluttering, restyling, and gap identification at your private residence. Includes outfit photography and a prioritised Wardrobe Gap List.' },
  { name: 'Personal Shopping', format: 'In-Person', price: '€240 / 95 000 Ft', scope: '3-hour dedicated in-person shopping session. Preceded by my independent route and merchandise curation; includes pre-selected items and hands-on fitting room styling.' },
  { name: 'Private Sourcing', format: 'Fully Online', price: '€200 / 80 000 Ft', scope: 'Sourcing up to 8 bespoke items (as a targeted wishlist or focused alternatives) delivered as a clickable digital edit with direct retail links, sizing guidance, and styling notes. Includes 1 Style Clarity Call (30 mins) and 1 refinement round (up to 3 replacement items).' },
];

const TermsPage = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#faf7ee] text-neutral-800" data-testid="terms-page">
      {/* Top bar */}
      <header className="sticky top-0 z-10 border-b border-neutral-200 bg-[#faf7ee]/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-home-logo">
            <img src="/s-mark.png" alt="Soulboard" className="h-9 w-auto" style={{ mixBlendMode: 'multiply' }} />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-neutral-900 transition-colors"
            data-testid="terms-back-link"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            {t('backToHome')}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 lg:py-20">
        <p className="text-[11px] tracking-[0.45em] uppercase text-neutral-500">Soulboard</p>
        <h1
          className="mt-3 leading-[1.0] text-neutral-900"
          style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2.25rem, 6vw, 3.5rem)' }}
        >
          {t('termsLinkLabel')}
        </h1>
        <p className="mt-3 text-base italic text-neutral-600">Soulboard · Curated Identity &amp; Style Services by Melinda Tóth</p>
        <p className="mt-2 text-[11px] tracking-[0.3em] uppercase text-neutral-500" data-testid="terms-effective-date">Effective date: October 2026</p>

        <div className="mt-12 space-y-10">
          <Section title="1. The Foundation of Our Work">
            <p>
              These Terms and Conditions form the framework for our collaboration. They govern the application for,
              booking of, and delivery of all services provided by Melinda Tóth under the brand Soulboard
              (&ldquo;I&rdquo;, &ldquo;me&rdquo;, or &ldquo;my&rdquo;).
            </p>
            <p>
              By submitting an application, confirming a commission, and completing payment, you (&ldquo;the
              client&rdquo; or &ldquo;you&rdquo;) accept and agree to these Terms. Soulboard is operated by me as an
              individual sole service provider. Where you are a consumer resident in the European Union, these Terms
              uphold and respect all mandatory statutory consumer-protection rights granted under EU and Hungarian law.
            </p>
          </Section>

          <Section title="2. Services, Investment, and Scope">
            <p>
              I offer a curated ecosystem of visual identity, wardrobe transformation, and personal styling services.
              The specific scope of each commission is detailed below and confirmed individually prior to payment:
            </p>
            <div className="border-t border-neutral-300" data-testid="terms-services-list">
              {TERMS_SERVICES.map((s) => (
                <div key={s.name} className="border-b border-neutral-300 py-5 space-y-2">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <p className="text-neutral-900" style={{ fontWeight: 600 }}>{s.name}</p>
                    <p className="text-neutral-900 font-medium whitespace-nowrap">{s.price}</p>
                  </div>
                  <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: '#7d65b8' }}>{s.format}</p>
                  <p>{s.scope}</p>
                </div>
              ))}
            </div>
            <Sub>2.1 Nature of the Collaboration</Sub>
            <p>
              All Soulboard services are personal, interpretive, and advisory. They are designed as instruments for
              aesthetic clarification, self-discovery, and wardrobe direction. Astrology is utilised strictly as a
              symbolic, psychological language and creative tool, not as fortune-telling or prediction. Soulboard
              services do not diagnose, treat, or substitute for professional medical, psychiatric, psychological,
              legal, or financial advice, nor can they guarantee specific commercial, emotional, or personal outcomes.
            </p>
          </Section>

          <Section title="3. Application, Booking, and Confirmation">
            <p>To protect the bespoke quality and focus of each commission, automated instant checkout is not provided.</p>
            <p><strong className="text-neutral-900">Application:</strong> You submit an application form outlining your desired offering, style intentions, and initial data.</p>
            <p><strong className="text-neutral-900">Review &amp; Alignment:</strong> I review your submission. If our aesthetic scope and schedules align, I confirm acceptance and issue payment details.</p>
            <p><strong className="text-neutral-900">Confirmation:</strong> Your commission or session dates are secured only upon receipt of cleared, full payment upfront. I reserve the right to decline any application at my sole discretion.</p>
          </Section>

          <Section title="4. Financial Terms">
            <p>Prices are stated in both Euros (EUR) and Hungarian Forint (HUF).</p>
            <p><strong className="text-neutral-900">VAT Status:</strong> Under Hungarian tax legislation for individual sole service providers (alanyi adómentes), prices are final and exempt from VAT (AAM).</p>
            <p>Any bank transfer, credit card transaction, or currency conversion fees charged by your financial institution remain your responsibility.</p>
            <p>Preparation, chart analysis, or calendar reservations commence strictly after cleared funds are received.</p>
          </Section>

          <Section title="5. Commitment, Withdrawal, and Cancellation Rights">
            <p>
              As a consumer in the European Union, you generally retain the statutory right to withdraw from a distance
              contract within 14 days without giving reasons. However, by statutory exemption under EU Directive
              2011/83/EU and Hungarian Government Decree 45/2014 (II. 26.):
            </p>
            <p>
              <strong className="text-neutral-900">Bespoke Digital Content &amp; Art Books (Signature Soulboard):</strong>{' '}
              Because your Soulboard is an intensely personalised, custom-crafted digital artwork prepared to your
              individual specifications from the moment work begins, you explicitly request immediate commencement of
              performance and acknowledge that you lose your statutory right of withdrawal once digital materials are
              generated and delivered. Completed digital deliverables are non-refundable.
            </p>
            <p>
              <strong className="text-neutral-900">Consulting, Method Sessions, and Sourcing:</strong> By scheduling a
              live appointment or commissioning a search, you request immediate preparatory work. Completed sessions and
              delivered sourcing edits are non-refundable.
            </p>
          </Section>

          <Section title="6. Scheduling, Session Etiquette, and In-Person Logistics">
            <Sub>6.1 Online Sessions (Method Blueprint &amp; Clarity Calls)</Sub>
            <p>Online sessions are conducted via video conferencing (Zoom/Google Meet).</p>
            <p>
              <strong className="text-neutral-900">Rescheduling:</strong> A minimum of 48 hours&rsquo; written notice is
              required to reschedule any booked session. Rescheduling requests made with less than 48 hours&rsquo;
              notice, or failure to attend without notice (no-show), will result in the session being forfeited without
              refund.
            </p>
            <p>If unforeseen emergency or illness requires me to adjust our schedule, I will provide prompt notice and offer priority alternative dates immediately.</p>
            <Sub>6.2 In-Person Services (Wardrobe Edit, Personal Shopping, The Style Shift)</Sub>
            <p>
              <strong className="text-neutral-900">Meeting Points (Personal Shopping):</strong> Personal Shopping
              sessions commence at an agreed, central location (e.g., a specific café or the first retail store). Each
              party is solely responsible for arranging and funding their own transportation to and from the initial
              meeting point.
            </p>
            <p>
              <strong className="text-neutral-900">Intra-Session Transit &amp; Taxi Policy:</strong> Personal shopping
              routes are curated to be walkable within a specific shopping district. If transit between distinct
              shopping areas is required, or if you prefer/request taxi or private vehicle transport during the session,
              all taxi fares, ride-hailing fees, or transit tickets incurred during the session are covered directly and
              settled by you at the time of transit.
            </p>
            <p>
              <strong className="text-neutral-900">Residence Access (Wardrobe Edit):</strong> You agree to provide a
              safe, respectful, and adequately lit environment at your residence for the 3-hour wardrobe session.
              Clothing to be evaluated must be reasonably clean and accessible. Local travel within the primary urban
              operating zone is included; any travel outside this metropolitan perimeter must be agreed upon and
              compensated separately in advance.
            </p>
            <p>
              <strong className="text-neutral-900">Punctuality:</strong> In-person sessions run strictly according to
              the agreed schedule. Time lost due to late arrival by the client will reduce the total duration of the
              appointment and will not be extended or refunded.
            </p>
          </Section>

          <Section title="7. Sourcing, Retail Links, and Third-Party Transactions">
            <p className="italic">(Applies to Private Sourcing, Personal Shopping, and The Style Shift)</p>
            <p>
              <strong className="text-neutral-900">Advisory Role Only:</strong> All sourcing catalogues, moodboards, and
              digital edits provide advisory styling recommendations and direct shopping links. I do not purchase
              clothing or merchandise on your behalf, nor do I handle retailer payments. All commercial transactions are
              made independently and directly between you and the respective third-party retailers.
            </p>
            <p>
              <strong className="text-neutral-900">Inventory &amp; Stock Dynamics:</strong> Availability and pricing of
              recommended garments are verified exclusively at the exact time your digital edit is released. Because
              external fashion inventories change dynamically, prompt purchasing is advised; I cannot guarantee future
              availability, sale continuity, or stock replenishment.
            </p>
            <p>
              <strong className="text-neutral-900">Fit, Sizing, and Exchanges:</strong> Sizing recommendations are
              indicative, based upon the measurements and brand sizing data you submit. You bear sole responsibility for
              verifying fit, ordering appropriate sizes, and managing any exchanges, alterations, or return procedures
              directly with vendors.
            </p>
            <p>
              <strong className="text-neutral-900">Refinement Protocol (Private Sourcing):</strong> Includes one
              refinement round with up to 3 replacement items, strictly valid within 7 calendar days of delivery, and
              constrained to the originally agreed brief and budget.
            </p>
          </Section>

          <Section title="8. Client Responsibilities & Intake Accuracy">
            <p>The outcome of your Soulboard depends directly on the depth, honesty, and promptness of your input:</p>
            <p>You agree to provide accurate birth coordinates (date, time, location), measurements, candid wardrobe photographs, and preference questionnaires prior to scheduled cut-offs.</p>
            <p>You confirm that you hold all legal rights to any personal photographs or moodboard materials you provide.</p>
            <p>Delays in supplying required intake materials may result in the postponement of your delivery or session date.</p>
          </Section>

          <Section title="9. Intellectual Property">
            <p>All creative frameworks, artistic layouts, custom prose, archetypal analyses, PDFs, and Wardrobe Blueprints created for you remain the exclusive intellectual property of Melinda Tóth / Soulboard.</p>
            <p>You are granted an exclusive, perpetual, non-transferable licence for your personal, private, non-commercial use and reflection.</p>
            <p>You may not copy, reproduce, publish, broadcast, resell, distribute, or repurpose any Soulboard materials for commercial gain without my prior written consent.</p>
            <p>Any cultural, photographic, or museum archival materials incorporated into your Soulboard remain the property of their respective copyright holders and are curated strictly as personal artistic reference within your private commission.</p>
          </Section>

          <Section title="10. Privacy, Discretion, and Sensitive Data (GDPR)">
            <p>Your privacy is treated with the highest degree of discretion and professional confidentiality:</p>
            <p>In accordance with EU Regulation 2016/679 (GDPR), your personal data (name, contact details, birth chart details, measurements, lifestyle reflections, and wardrobe/portrait photographs) is collected and processed strictly on the basis of your explicit consent and solely to curate and deliver your commission.</p>
            <p>I will never publish your name, personal birth data, photographs, or intimate wardrobe notes publicly or on social media without obtaining your explicit, separate, written consent beforehand.</p>
            <p>You maintain statutory rights to access, rectify, or request the deletion of your personal data at any time, subject to statutory accounting retention obligations.</p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p>Nothing in these Terms excludes or limits liability that cannot legally be excluded under applicable law, including liability for intent, gross negligence, fraudulent misrepresentation, or mandatory statutory consumer rights.</p>
            <p>Beyond mandatory statutory obligations, Soulboard&rsquo;s insights and curations are advisory. To the fullest extent permitted by law, I shall not be liable for any indirect, incidental, or consequential loss, nor for personal style choices, wardrobe dissatisfaction, third-party retailer disputes, delivery damages, or alteration results.</p>
            <p>Where liability cannot be fully disclaimed by law, my aggregate financial liability to you for any claim arising out of a service shall not exceed the total fee actually paid by you for that specific commission.</p>
          </Section>

          <Section title="12. Governing Law and Dispute Resolution">
            <p>These Terms and Conditions are governed by and construed in accordance with the laws of Hungary.</p>
            <p>Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts of Hungary, without prejudice to any mandatory consumer rights granted by the courts of your EU Member State of habitual residence.</p>
          </Section>

          <Section title="Contact & Inquiries">
            <p>For questions regarding these Terms, bookings, or data protection:</p>
            <p className="text-neutral-900">
              Melinda Tóth
              <br />
              Founder &amp; Creative Director, Soulboard
              <br />
              Email:{' '}
              <a href="mailto:hello@soulboard.me" className="underline hover:text-neutral-700" data-testid="terms-contact-email">
                hello@soulboard.me
              </a>
            </p>
          </Section>
        </div>

        <div className="mt-14 pt-8 border-t border-neutral-200">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-neutral-900 hover:opacity-70 transition-opacity"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            {t('backToHome')}
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
